import {
  UPDATE_DATA_RETENTION_POLICIES,
  UPDATE_ORGANISATION_PII_SETTINGS,
} from "@lib/graphql/mutations";
import { ORGANISATION_SETTINGS_QUERY } from "@lib/graphql/queries";
import { useState } from "react";
import { useMutation, useQuery } from "urql";

interface RetentionPolicy {
  resourceType: string;
  retentionDays: number;
  enabled: boolean;
}

const RESOURCE_TYPES = [
  { key: "candidate_profile", label: "Candidate Profiles" },
  { key: "resume", label: "Resumes" },
  { key: "interview_notes", label: "Interview Notes" },
  { key: "contact_info", label: "Contact Information" },
];

const DEFAULT_RETENTION_DAYS = 730;

export function DataRetentionSettings() {
  const [{ data }, reexecute] = useQuery({ query: ORGANISATION_SETTINGS_QUERY });
  const [, updatePolicies] = useMutation(UPDATE_DATA_RETENTION_POLICIES);
  const [, updatePiiSettings] = useMutation(UPDATE_ORGANISATION_PII_SETTINGS);

  const org = data?.currentOrganisation;
  const isAdmin = org?.currentUserRole === "admin";
  const piiMaskingEnabled = (org?.piiMaskingEnabled as boolean) ?? false;
  const savedPolicies: RetentionPolicy[] = (org?.dataRetentionPolicies || []) as RetentionPolicy[];

  const initialPolicies = RESOURCE_TYPES.map(({ key }) => {
    const existing = savedPolicies.find((p) => p.resourceType === key);
    return {
      resourceType: key,
      retentionDays: existing?.retentionDays ?? DEFAULT_RETENTION_DAYS,
      enabled: existing?.enabled ?? false,
    };
  });

  const [policies, setPolicies] = useState<RetentionPolicy[]>(initialPolicies);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  // Sync when data loads
  if (!dirty) {
    const synced = RESOURCE_TYPES.map(({ key }) => {
      const existing = savedPolicies.find((p) => p.resourceType === key);
      return {
        resourceType: key,
        retentionDays: existing?.retentionDays ?? DEFAULT_RETENTION_DAYS,
        enabled: existing?.enabled ?? false,
      };
    });
    const needsSync = synced.some(
      (s, i) => s.retentionDays !== policies[i].retentionDays || s.enabled !== policies[i].enabled,
    );
    if (needsSync) {
      setPolicies(synced);
    }
  }

  function updatePolicy(index: number, field: keyof RetentionPolicy, value: number | boolean) {
    setPolicies((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setDirty(true);
    setSuccess(false);
  }

  async function handleSave() {
    setSaving(true);
    setErrors([]);
    setSuccess(false);

    const result = await updatePolicies({
      input: {
        policies: policies.map((p) => ({
          resourceType: p.resourceType,
          retentionDays: p.retentionDays,
          enabled: p.enabled,
        })),
      },
    });

    const mutationErrors = result.data?.updateDataRetentionPolicies?.errors || [];
    if (mutationErrors.length === 0) {
      setDirty(false);
      setSuccess(true);
      reexecute({ requestPolicy: "network-only" });
    } else {
      setErrors(mutationErrors);
    }
    setSaving(false);
  }

  const [piiSaving, setPiiSaving] = useState(false);
  const [piiSuccess, setPiiSuccess] = useState(false);

  async function handlePiiToggle() {
    setPiiSaving(true);
    setPiiSuccess(false);
    const result = await updatePiiSettings({
      input: { piiMaskingEnabled: !piiMaskingEnabled },
    });
    const mutationErrors = result.data?.updateOrganisationPiiSettings?.errors || [];
    if (mutationErrors.length === 0) {
      setPiiSuccess(true);
      reexecute({ requestPolicy: "network-only" });
    }
    setPiiSaving(false);
  }

  return (
    <div className="mt-6 space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">PII Masking</h2>
        <p className="mt-1 text-sm text-text-muted">
          When enabled, candidate email and phone are masked in the hiring manager portal and PDF
          reports. Portal users can reveal PII on a per-candidate basis (logged for audit).
        </p>
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handlePiiToggle}
            disabled={!isAdmin || piiSaving}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
              piiMaskingEnabled ? "bg-accent" : "bg-gray-300 dark:bg-gray-600"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                piiMaskingEnabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
          <span className="text-sm text-text-primary">
            {piiMaskingEnabled ? "Enabled" : "Disabled"}
          </span>
          {piiSuccess && <span className="text-sm text-green-600 dark:text-green-400">Saved.</span>}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-text-primary">Data Retention Policies</h2>
        <p className="mt-1 text-sm text-text-muted">
          Configure how long different types of candidate data are retained. Policies that are
          enabled will be enforced automatically when data deletion is run.
        </p>
      </div>

      <div className="overflow-hidden rounded-lg border border-input-border">
        <table className="min-w-full divide-y divide-input-border">
          <thead className="bg-card">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-muted">
                Data Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-muted">
                Retention Period (days)
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-muted">
                Enabled
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-input-border bg-card">
            {policies.map((policy, index) => {
              const label = RESOURCE_TYPES.find((r) => r.key === policy.resourceType)?.label;
              return (
                <tr key={policy.resourceType}>
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-text-primary">
                    {label}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <input
                      type="number"
                      min={1}
                      value={policy.retentionDays}
                      onChange={(e) =>
                        updatePolicy(
                          index,
                          "retentionDays",
                          Number.parseInt(e.target.value, 10) || 1,
                        )
                      }
                      disabled={!isAdmin}
                      className="w-32 rounded-md border border-input-border bg-card px-3 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
                    />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <button
                      type="button"
                      onClick={() => updatePolicy(index, "enabled", !policy.enabled)}
                      disabled={!isAdmin}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                        policy.enabled ? "bg-accent" : "bg-gray-300 dark:bg-gray-600"
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          policy.enabled ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {errors.length > 0 && (
        <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
          {errors.map((error) => (
            <p key={error} className="text-sm text-red-700 dark:text-red-400">
              {error}
            </p>
          ))}
        </div>
      )}
      {success && (
        <p className="text-sm text-green-600 dark:text-green-400">Data retention policies saved.</p>
      )}

      {isAdmin && (
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Retention Policies"}
        </button>
      )}
    </div>
  );
}
